import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "@/components/Motion";
import { PRODUCTS, SITE, buildWhatsAppLink } from "@/data/site";
import { useLang, TA, u } from "@/data/i18n";

export default function ProductPage() {
  const { id } = useParams();
  const { lang } = useLang();
  const product = PRODUCTS.find((p) => p.id === id);
  const pta = lang === "ta" ? TA.products[id] : null;

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    if (product) document.title = `${product.name} — Spec Sheet | Lovely Foodpacks, Sivakasi`;
    return () => {
      document.title = "Lovely Foodpacks | Paper Banana Leaf Manufacturer Sivakasi | Eco Food Packaging Since 2000";
    };
  }, [id, product]);

  if (!product) return <Navigate to="/" replace />;

  const others = PRODUCTS.filter((p) => p.id !== id);

  return (
    <main data-testid="product-page" className="bg-paper">
      {/* Header band */}
      <section className="noise-overlay relative bg-leaf text-white pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <span aria-hidden className="text-stroke-white pointer-events-none select-none absolute -bottom-4 right-0 font-display font-black text-[18vw] leading-none tracking-tighter whitespace-nowrap">
          SPEC
        </span>
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10">
          <Link
            to="/"
            data-testid="product-back-link"
            className="group inline-flex items-center gap-2 text-white/60 text-sm font-bold tracking-wide transition-colors duration-300 hover:text-sun"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            {u(lang, "back_catalogue")}
          </Link>
          <div className="mt-8">
            <MaskedLine delay={0.1}>
              <span className="inline-block bg-sun text-leaf text-[11px] font-bold tracking-[0.25em] uppercase px-3 py-1.5">
                {pta?.tag || product.tag}
              </span>
            </MaskedLine>
            <h1 className="mt-5 font-display font-black tracking-tighter leading-[0.95] text-5xl md:text-7xl">
              <MaskedLine delay={0.25}>{pta?.name || product.name}</MaskedLine>
            </h1>
            <MaskedLine delay={0.4}>
              <p className="mt-6 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">{pta?.desc || product.desc}</p>
            </MaskedLine>
          </div>
        </div>
      </section>

      {/* Spec sheet + hero image */}
      <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <FadeUp>
            <div className="flex items-center gap-3 mb-6">
              <FileText size={22} className="text-leaf-bright" />
              <Overline>{u(lang, "spec_sheet")}</Overline>
            </div>
            <div data-testid="spec-table" className="border border-ink/10 bg-white">
              {product.specs.map(([label, value], i) => (
                <div
                  key={label}
                  className={`grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-6 px-6 md:px-8 py-5 ${
                    i !== 0 ? "border-t border-ink/10" : ""
                  } transition-colors duration-300 hover:bg-paper`}
                >
                  <span className="sm:col-span-4 text-[11px] md:text-xs tracking-[0.2em] uppercase font-bold text-leaf-bright self-center">
                    {lang === "ta" ? TA.specLabels[label] ?? label : label}
                  </span>
                  <span className="sm:col-span-8 text-sm md:text-base text-ink font-medium leading-relaxed">{value}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted leading-relaxed">
              {u(lang, "spec_note")}
            </p>
            {product.alsoKnownAs && (
              <div className="mt-6" data-testid="also-searched">
                <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-leaf-bright mb-3">
                  {u(lang, "also_searched")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.alsoKnownAs.map((k) => (
                    <span key={k} className="text-xs font-semibold border border-leaf/20 text-leaf px-3 py-1.5 bg-paper">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                data-testid="spec-whatsapp-btn"
                href={buildWhatsAppLink(`Hello Lovely Foodpacks! I've reviewed the ${product.name} spec sheet. Please share wholesale pricing, MOQ & availability.`)}
                target="_blank"
                rel="noreferrer"
                className="group flex-1 inline-flex items-center justify-center gap-2 bg-leaf text-white px-7 py-4 font-bold tracking-wide transition-colors duration-300 hover:bg-sun hover:text-leaf"
              >
                {u(lang, "wholesale_pricing")}
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                data-testid="spec-indiamart-btn"
                href={SITE.indiamart}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-leaf/30 text-leaf px-7 py-4 font-bold tracking-wide transition-colors duration-300 hover:border-sun hover:text-leaf-bright"
              >
                {u(lang, "view_indiamart")}
              </a>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-5">
          <FadeUp delay={0.15}>
            <div className="border border-ink/10 bg-white p-3 shadow-2xl">
              <img src={product.img} alt={`${product.name} — Lovely Foodpacks Sivakasi`} className="w-full object-cover" data-testid="spec-main-image" />
            </div>
          </FadeUp>
          {product.gallery.length > 1 && (
            <div className="mt-5 grid grid-cols-3 gap-3">
              {product.gallery.slice(1, 4).map((g, i) => (
                <FadeUp key={g} delay={0.2 + 0.06 * i}>
                  <div className="border border-ink/10 overflow-hidden bg-white group">
                    <img
                      src={g}
                      alt={`${product.name} view ${i + 2}`}
                      loading="lazy"
                      className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other products */}
      <section className="max-w-[1500px] mx-auto px-5 md:px-10 pb-24">
        <FadeUp>
          <Overline>{u(lang, "also_catalogue")}</Overline>
        </FadeUp>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {others.map((p, i) => (
            <FadeUp key={p.id} delay={0.05 * i}>
              <Link
                to={`/product/${p.id}`}
                data-testid={`related-${p.id}`}
                className="group block border border-ink/10 bg-white overflow-hidden"
              >
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 border-t border-ink/10 flex items-center justify-between gap-2">
                  <span className="font-display font-bold text-sm text-leaf tracking-tight leading-tight">
                    {lang === "ta" ? TA.products[p.id].name : p.name}
                  </span>
                  <ArrowUpRight size={15} className="shrink-0 text-muted transition-all duration-300 group-hover:text-leaf-bright group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>
    </main>
  );
}
